import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '@/components/Logo';

const navLinks = [
  { label: 'About', to: '#who-we-are' },
  { label: 'How We Work', to: '#how-we-work' },
  { label: 'Investments', to: '#investments' },
  { label: 'Contact', to: '#contact' },
];

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  const showSolid = !transparent || scrolled;

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    const id = hash.replace('#', '');

    if (location.pathname !== '/') {
      navigate(`/${hash}`);
      return;
    }

    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      navigate(hash, { replace: true });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-8 lg:px-12 transition-all duration-500 ${
        showSolid ? 'pt-3' : 'pt-6'
      }`}
    >
      <div
        className={`mx-auto max-w-7xl flex items-center justify-between px-4 py-5 transition-all duration-500 ${
          showSolid ? 'backdrop-blur-sm [mask-image:linear-gradient(to_right,transparent_0%,black_12%,black_88%,transparent_100%)]' : ''
        }`}
      >
        <a
          href="#about"
          onClick={(e) => handleNavClick(e, '#about')}
          className="flex items-center gap-2.5 group cursor-pointer"
        >
          <Logo className="h-[22px] w-auto"
                style={{ filter: 'drop-shadow(0 0 10px rgba(168,139,210,0.35)) drop-shadow(0 0 3px rgba(168,139,210,0.2))' }}
          />
        </a>

        <div className="hidden md:flex gap-1 text-sm">
          {navLinks.map((link) => {
            const active = location.hash === link.to;
            return (
              <a
                key={link.to}
                href={link.to}
                onClick={(e) => handleNavClick(e, link.to)}
                className={`relative px-4 py-2 rounded-lg transition-colors hover-shine cursor-pointer ${
                  active ? 'text-white' : 'text-white/70 hover:text-white'
                }`}
              >
                {link.label}
                {active && (
                  <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-gradient-to-r from-transparent via-primary-400 to-transparent" />
                )}
              </a>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-white/15 text-white cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-lg`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden mx-auto max-w-7xl overflow-hidden transition-all duration-500 ${
          mobileOpen ? 'max-h-96 mt-3' : 'max-h-0'
        }`}
      >
        <div className="rounded-2xl p-4 flex flex-col gap-1 bg-black/80">
          {navLinks.map((link) => (
            <a
              key={link.to}
              href={link.to}
              onClick={(e) => handleNavClick(e, link.to)}
              className="px-3 py-2 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="mt-2 bg-primary-500 text-white px-4 py-2 rounded-lg text-sm font-medium text-center cursor-pointer"
          >
            Get in Touch
          </a>
        </div>
      </div>
    </nav>
  );
}